import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { themes } from "../index";
import type { Theme, ThemeName } from "../semantic/types";
import {
  isThemeDebugSyncEnabled,
  isValidThemeName,
  readThemeDebugFromSession,
  subscribeToThemeDebugEvents,
  type ThemeDebugEventDetail,
} from "./themeDebug";
import type { ColorScale, ThemeColors } from "../semantic/colors";
import { toWebStyles } from "../semantic/typography";
import { getMainScreenChromeGradient } from "../mainScreenChrome";

type ResolvedTheme = Omit<Theme, "colors"> & {
  colors: Omit<ThemeColors, "subject"> & {
    subject: Readonly<Record<string, ColorScale>>;
  };
};

interface ThemeContextType {
  theme: ResolvedTheme;
  themeName: ThemeName;
  subjectKey: string | null;
  setTheme: (name: ThemeName) => void;
  setSubjectKey: (key: string | null) => void;
  forceSync: () => void;
}

declare const document: any;

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function isFontStylesObject(obj: any): boolean {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof obj.fontSize === "number" &&
    typeof obj.lineHeight === "number" &&
    typeof obj.fontFamily === "string"
  );
}

function normalizeSubjectKey(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function normalizeSubjectCssKey(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, "-");
}

function wrapSubjectColors(
  subjectColors: Theme["colors"]["subject"],
  fallbackColors: Theme["colors"]["primary"],
) {
  return new Proxy(subjectColors, {
    get(target, prop, receiver) {
      if (typeof prop === "string") {
        return (
          Reflect.get(target, normalizeSubjectKey(prop), receiver) ??
          Reflect.get(target, prop, receiver) ??
          fallbackColors
        );
      }

      return Reflect.get(target, prop, receiver) ?? fallbackColors;
    },
  });
}

function wrapTheme(theme: Theme): ResolvedTheme {
  const isWeb = typeof document !== "undefined";

  function wrapTypography(obj: any): any {
    if (isFontStylesObject(obj)) {
      return toWebStyles(obj);
    }

    if (typeof obj === "object" && obj !== null && !Array.isArray(obj)) {
      return new Proxy(obj, {
        get(target, prop) {
          const value = target[prop as string];
          if (typeof value === "object" && value !== null) {
            return wrapTypography(value);
          }
          return value;
        },
      });
    }

    return obj;
  }

  return new Proxy(theme, {
    get(target, prop) {
      const value = target[prop as keyof Theme];

      if (prop === "colors" && typeof value === "object" && value !== null) {
        return new Proxy(value, {
          get(colorTarget, colorProp) {
            const typedColorTarget = colorTarget as Theme["colors"];
            const colorValue =
              typedColorTarget[colorProp as keyof Theme["colors"]];

            if (
              colorProp === "subject" &&
              typeof colorValue === "object" &&
              colorValue !== null
            ) {
              return wrapSubjectColors(
                colorValue as Theme["colors"]["subject"],
                typedColorTarget.primary,
              );
            }

            return colorValue;
          },
        });
      }

      if (
        isWeb &&
        prop === "typography" &&
        typeof value === "object" &&
        value !== null
      ) {
        return wrapTypography(value);
      }
      return value;
    },
  }) as ResolvedTheme;
}

export function ThemeProvider({
  children,
  initialTheme = "learn",
  initialSubjectKey = null,
  revertThemeDebugToInitialOnClear = false,
  themeDebugClearTo,
}: {
  children: ReactNode;
  initialTheme?: ThemeName;
  initialSubjectKey?: string | null;
  /** When theme debug is cleared, reset this provider to the current `initialTheme` (use in nested module apps). Host root should leave false so Auth-driven theme can apply. */
  revertThemeDebugToInitialOnClear?: boolean;
  /** When debug is cleared, revert to this theme (e.g. vertical from profile). Falls back to latest `initialTheme` if omitted. */
  themeDebugClearTo?: ThemeName;
}) {
  const initialThemeRef = useRef(initialTheme);
  useEffect(() => {
    initialThemeRef.current = initialTheme;
  }, [initialTheme]);

  const [themeName, setThemeName] = useState<ThemeName>(initialTheme);
  const [subjectKey, setSubjectKey] = useState<string | null>(
    initialSubjectKey,
  );
  const baseTheme = themes[themeName];

  useEffect(() => {
    if (!isThemeDebugSyncEnabled()) {
      return;
    }
    const fromSession = readThemeDebugFromSession();
    if (fromSession) {
      setThemeName(fromSession);
    }
  }, []);

  useEffect(() => {
    if (!isThemeDebugSyncEnabled()) {
      return;
    }
    return subscribeToThemeDebugEvents((detail: ThemeDebugEventDetail) => {
      if (detail.cleared) {
        if (revertThemeDebugToInitialOnClear) {
          setThemeName(themeDebugClearTo ?? initialThemeRef.current);
        }
        return;
      }
      if (detail.theme && isValidThemeName(detail.theme)) {
        setThemeName(detail.theme);
      }
    });
  }, [revertThemeDebugToInitialOnClear, themeDebugClearTo]);

  const theme = useMemo(() => wrapTheme(baseTheme), [baseTheme]);
  const activeSubjectColors = useMemo(
    () =>
      subjectKey && subjectKey.trim().length > 0
        ? theme.colors.subject[subjectKey]
        : theme.colors.primary,
    [subjectKey, theme],
  );

  const [syncTrigger, setSyncTrigger] = useState(0);
  const forceSync = useCallback(() => setSyncTrigger((prev) => prev + 1), []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      const root = document.documentElement;
      const setVariables = (
        obj: any,
        prefix = "",
        path = "",
        parentFontSize?: number,
      ) => {
        if (isFontStylesObject(obj)) {
          const fontSize = obj.fontSize;
          const lineHeight = obj.lineHeight;
          root.style.setProperty(`--${prefix}color`, obj.color);
          root.style.setProperty(`--${prefix}fontSize`, `${fontSize / 16}rem`);
          root.style.setProperty(
            `--${prefix}lineHeight`,
            String(lineHeight / fontSize),
          );
          root.style.setProperty(`--${prefix}fontFamily`, obj.fontFamily);
          root.style.setProperty(
            `--${prefix}fontWeight`,
            String(obj.fontWeight),
          );
          root.style.setProperty(
            `--${prefix}letterSpacing`,
            `${obj.letterSpacing}px`,
          );
          return;
        }

        for (const [key, value] of Object.entries(obj)) {
          const isSubjectKey = path === "colors.subject";
          const cssKey = isSubjectKey ? normalizeSubjectCssKey(key) : key;
          const currentPath = path ? `${path}.${key}` : key;

          if (
            typeof value === "object" &&
            value !== null &&
            !Array.isArray(value)
          ) {
            let fontSize = parentFontSize;
            if (isFontStylesObject(value)) {
              fontSize = (value as any).fontSize;
            }
            setVariables(value, `${prefix}${cssKey}-`, currentPath, fontSize);
          } else {
            let finalValue = String(value);

            if (typeof value === "number") {
              if (
                key === "lineHeight" &&
                currentPath.includes("typography") &&
                parentFontSize
              ) {
                finalValue = String(value / parentFontSize);
              } else if (key === "fontSize") {
                finalValue = `${value / 16}rem`;
              } else if (
                currentPath.includes("spacing") ||
                currentPath.includes("radii")
              ) {
                finalValue = `${value / 16}rem`;
              } else if (
                currentPath.includes("width") ||
                currentPath.includes("shadows") ||
                currentPath.includes("border")
              ) {
                finalValue = `${value / 16}rem`;
              }
            }

            root.style.setProperty(`--${prefix}${cssKey}`, finalValue);
          }
        }
      };

      setVariables(baseTheme);

      const [chromeFrom, chromeTo] = getMainScreenChromeGradient(baseTheme);
      root.style.setProperty("--main-screen-chrome-from", chromeFrom);
      root.style.setProperty("--main-screen-chrome-to", chromeTo);

      for (const [key, value] of Object.entries(activeSubjectColors)) {
        root.style.setProperty(`--subject-${key}`, String(value));
      }
    }
  }, [activeSubjectColors, baseTheme, syncTrigger]);

  const value = useMemo(
    () => ({
      theme,
      themeName,
      subjectKey,
      setTheme: setThemeName,
      setSubjectKey,
      forceSync,
    }),
    [theme, themeName, subjectKey, forceSync],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
